import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { AdminHeader, StatusBadge, dateBadge, sportName } from "./adminShared";
import { SportIcon } from "@/components/SportIcon";
import { GrantListingModal, GrantFeaturedModal, CompGrantsPanel } from "./AdminCompModals";
import { Search, Loader2, Eye, Star, Trash2, Pencil, Sparkles, Gift } from "lucide-react";

// mode: "all" | "submissions" | "featured"
export default function AdminRaces({ mode = "all" }) {
  const [races, setRaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(mode === "all" ? (searchParams.get("status") || "") : "");
  useEffect(() => { if (mode === "all") setStatusFilter(searchParams.get("status") || ""); }, [searchParams, mode]);
  const [demoCount, setDemoCount] = useState(0);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purging, setPurging] = useState(false);
  const [grantListing, setGrantListing] = useState(false);
  const [grantFeatured, setGrantFeatured] = useState(null); // race object
  const [grantsKey, setGrantsKey] = useState(0);

  const loadDemoCount = useCallback(() => {
    api.get("/admin/demo/count").then(({ data }) => setDemoCount(data.count)).catch(() => {});
  }, []);

  const purge = async (mode) => {
    setPurging(true);
    try {
      const { data } = await api.post(`/admin/demo/purge?mode=${mode}`);
      toast.success(mode === "delete" ? `Deleted ${data.affected} demo race(s)` : `Hid ${data.affected} demo race(s)`);
      setPurgeOpen(false);
      loadDemoCount();
      load();
    } catch { toast.error("Purge failed"); } finally { setPurging(false); }
  };

  const load = useCallback(() => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (mode === "submissions") qs.set("status", "pending_approval");
    else if (statusFilter) qs.set("status", statusFilter);
    if (search) qs.set("search", search);
    api.get(`/admin/races?${qs}`).then(({ data }) => {
      let rs = data.races;
      if (mode === "featured") rs = rs.filter((r) => r.featured);
      setRaces(rs);
    }).finally(() => setLoading(false));
  }, [mode, statusFilter, search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadDemoCount(); }, [loadDemoCount]);

  const feature = async (id, val) => { try { await api.post(`/admin/races/${id}/feature?featured=${val}`); toast.success(val ? "Featured" : "Unfeatured"); load(); } catch { toast.error("Failed"); } };
  const del = async (id) => { if (!window.confirm("Delete this race permanently?")) return; try { await api.delete(`/admin/races/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };

  const title = mode === "submissions" ? "Submissions" : mode === "featured" ? "Featured Races" : "Races";
  const subtitle = mode === "submissions" ? "Race listings pending your review and approval." : mode === "featured" ? "Races currently boosted with higher visibility." : "Review, approve, publish, feature or remove any race.";

  return (
    <div>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <AdminHeader title={title} subtitle={subtitle} />
        <div className="flex items-center gap-2 shrink-0">
          {mode !== "featured" && (
            <button onClick={() => setGrantListing(true)} data-testid="grant-free-listing-btn"
              className="inline-flex items-center gap-2 bg-emerald-500 text-white text-sm font-bold px-4 py-2.5 rounded-lg hover:bg-emerald-600">
              <Gift size={15} /> Grant Free Listing
            </button>
          )}
          {mode !== "submissions" && (
            <button onClick={() => setPurgeOpen(true)} disabled={demoCount === 0} data-testid="admin-demo-purge-btn"
              className="inline-flex items-center gap-2 bg-[#0a1420] text-white text-sm font-bold px-4 py-2.5 rounded-lg hover:bg-[#1a2740] disabled:opacity-40 disabled:cursor-not-allowed">
              <Sparkles size={15} className="text-[#22d3ee]" /> Purge Demo{demoCount > 0 && <span className="bg-[#22d3ee] text-[#0a1420] text-[11px] px-1.5 py-0.5 rounded-full">{demoCount}</span>}
            </button>
          )}
        </div>
      </div>

      {purgeOpen && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" onClick={() => !purging && setPurgeOpen(false)} data-testid="admin-demo-purge-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-xl uppercase text-slate-800 flex items-center gap-2"><Sparkles size={18} className="text-[#22d3ee]" /> Purge Demo Content</h3>
            <p className="text-sm text-slate-500 mt-2">You have <b>{demoCount}</b> demo race{demoCount === 1 ? "" : "s"} plus their demo organizers, users, payments and activity. Choose how to remove them before real listings go live.</p>
            <div className="mt-5 space-y-3">
              <button onClick={() => purge("hide")} disabled={purging} data-testid="admin-demo-purge-hide"
                className="w-full text-left border border-slate-300 rounded-xl p-4 hover:border-[#22d3ee] hover:bg-slate-50 disabled:opacity-50">
                <div className="font-bold text-slate-800">Hide All Demo Races <span className="text-xs font-semibold text-emerald-600">· Reversible</span></div>
                <div className="text-sm text-slate-500 mt-0.5">Moves every demo race to Draft, removing them from the public site. You can re-publish them later.</div>
              </button>
              <button onClick={() => purge("delete")} disabled={purging} data-testid="admin-demo-purge-delete"
                className="w-full text-left border border-red-200 rounded-xl p-4 hover:border-red-400 hover:bg-red-50 disabled:opacity-50">
                <div className="font-bold text-red-600">Purge All Demo Data <span className="text-xs font-semibold text-red-500">· Permanent</span></div>
                <div className="text-sm text-slate-500 mt-0.5">Permanently removes all demo races, organizers, users, submissions, payments and related demo activity. Real production data is never touched. This cannot be undone.</div>
              </button>
            </div>
            <div className="mt-4 flex justify-end">
              <button onClick={() => setPurgeOpen(false)} disabled={purging} className="text-sm font-semibold text-slate-500 px-4 py-2 hover:text-slate-800" data-testid="admin-demo-purge-cancel">
                {purging ? <Loader2 size={16} className="animate-spin" /> : "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search races or organizers..." data-testid="admin-races-search"
            className="w-full border border-slate-300 rounded-lg pl-9 pr-3 py-2 text-sm outline-none focus:border-[#22d3ee]" />
        </div>
        {mode === "all" && (
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
            <option value="">All statuses</option><option value="draft">Draft</option><option value="pending_approval">Pending</option><option value="approved">Approved</option><option value="published">Published</option><option value="rejected">Rejected</option>
          </select>
        )}
      </div>

      {loading ? <div className="py-16 grid place-items-center"><Loader2 className="animate-spin text-[#22d3ee]" /></div> :
        races.length === 0 ? <p className="py-16 text-center text-slate-500 bg-white border border-slate-200 rounded-xl">No races found.</p> : (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="admin-races-list">
            {races.map((r) => { const b = dateBadge(r.date); return (
              <div key={r.id} className="flex flex-wrap items-center gap-4 p-4 border-b border-slate-100 last:border-0" data-testid={`admin-race-row-${r.id}`}>
                <div className="w-14 h-14 rounded-lg overflow-hidden bg-slate-100 shrink-0">{r.hero_image && <img src={r.hero_image} alt="" className="w-full h-full object-cover" />}</div>
                <div className="flex-1 min-w-[160px]">
                  <div className="font-display font-bold uppercase text-slate-800 flex items-center gap-2">{r.name}{r.featured && <Star size={13} className="text-amber-500 fill-amber-500" />}</div>
                  <div className="text-sm text-slate-500 flex items-center gap-1.5"><SportIcon sport={r.sport} size={13} />{sportName(r.sport)} · {b.day} {b.month} {b.year} · {r.organizer_name}</div>
                  <div className="mt-1 flex items-center gap-1.5"><StatusBadge status={r.status} />{r.complimentary && <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded" data-testid={`comp-badge-${r.id}`}>COMP</span>}{r.featured_complimentary && r.featured && <span className="text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">FEAT·COMP</span>}{r.is_demo && <span className="text-[10px] font-bold bg-[#22d3ee] text-[#0a1420] px-1.5 py-0.5 rounded">DEMO</span>}</div>
                </div>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <Link to={`/admin/races/${r.id}`} className="inline-flex items-center gap-1 bg-[#0a1420] text-white text-xs font-bold px-3 py-2 rounded-lg" data-testid={`review-race-${r.id}`}>Review</Link>
                  {r.status === "published" && <a href={`/race/${r.slug}`} target="_blank" rel="noreferrer" className="p-2 border border-slate-300 rounded-lg hover:bg-slate-100" title="Preview as runner"><Eye size={15} /></a>}
                  <button onClick={() => feature(r.id, !r.featured)} className="p-2 border border-slate-300 rounded-lg hover:bg-amber-50" title="Toggle featured"><Star size={15} className={r.featured ? "text-amber-500 fill-amber-500" : ""} /></button>
                  {!r.featured && <button onClick={() => setGrantFeatured(r)} className="p-2 border border-slate-300 rounded-lg hover:bg-emerald-50 hover:text-emerald-600" title="Grant free featured" data-testid={`grant-featured-${r.id}`}><Gift size={15} /></button>}
                  <button onClick={() => del(r.id)} className="p-2 border border-slate-300 rounded-lg hover:bg-red-50 hover:text-red-500" title="Delete"><Trash2 size={15} /></button>
                </div>
              </div>
            ); })}
          </div>
        )}

      <CompGrantsPanel refreshKey={grantsKey} />

      {grantListing && <GrantListingModal onClose={() => setGrantListing(false)} onDone={() => { load(); setGrantsKey((k) => k + 1); }} />}
      {grantFeatured && <GrantFeaturedModal race={grantFeatured} onClose={() => setGrantFeatured(null)} onDone={() => { load(); setGrantsKey((k) => k + 1); }} />}
    </div>
  );
}
